<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaCard from '../base/GaiaCard.vue';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** card = bordered action row (wraps GaiaCard), flat = rail row with hairline divider. */
  variant: {
    type: String as PropType<'card' | 'flat'>,
    default: 'flat',
  },
  clickable: {
    type: Boolean,
    default: true,
  },
  /**
   * The row's text size. Belongs on the row, not only on the slotted GaiaText:
   * the leading glyph is centred against the content's *line box*, and a line
   * box is at least as tall as its container's strut. Left at the shell's 20px
   * body size while the text inside is 14px, that strut adds leading the copy
   * doesn't fill, and the glyph centres against the phantom height instead of
   * the letters. Undefined inherits — right when the row carries page copy.
   */
  size: {
    type: String as PropType<keyof typeof gaiaFontSize>,
    default: undefined,
  },
});

const sizeStyle = computed(() => (props.size ? { fontSize: gaiaFontSize[props.size] } : undefined));

const metaSize = gaiaFontSize.meta;
const metaColor = gaiaNeutral.textMuted;
const dividerColor = gaiaNeutral.borderSoft;
const medium = String(gaiaFontWeight.medium);
</script>

<template>
  <gaia-card
    v-if="props.variant === 'card'"
    :interactive="props.clickable"
    content-style="padding: 13px 16px"
  >
    <div class="row" :style="sizeStyle">
      <n-flex v-if="$slots.leading" align="center" :size="0" :wrap="false" class="leading">
        <slot name="leading" />
      </n-flex>
      <div class="content"><slot /></div>
      <span v-if="$slots.trailing" class="trailing"><slot name="trailing" /></span>
      <div v-if="$slots.footer" class="footer"><slot name="footer" /></div>
    </div>
  </gaia-card>
  <div v-else class="row flat" :class="{ clickable: props.clickable }" :style="sizeStyle">
    <n-flex v-if="$slots.leading" align="center" :size="0" :wrap="false" class="leading">
      <slot name="leading" />
    </n-flex>
    <div class="content"><slot /></div>
    <span v-if="$slots.trailing" class="trailing"><slot name="trailing" /></span>
    <div v-if="$slots.footer" class="footer"><slot name="footer" /></div>
  </div>
</template>

<style scoped>
/* Grid rather than n-flex/n-space: the leading slot has to centre against the
   content row *alone* while the footer sits below, in the content column. A
   flex row can only centre leading against content + footer together, and a
   nested column would un-indent the footer. Column gaps are per-item margins,
   not `column-gap`, so an absent leading/trailing leaves no phantom indent. */
.row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  --gaia-row-gap: 12px;
}

/* `display: flex` is what makes the grid's `align-items: center` reach the
   glyph. As a plain span this is a blockified grid item holding an inline-level
   child, so it grows to a full line box (~22px of inherited leading) and the
   glyph aligns to *that* box's baseline — sitting low, and lower the smaller it
   is. Flex drops the line box: the span is exactly as tall as the glyph, so
   centring the span centres the glyph against the content line beside it. */
.leading {
  grid-area: 1 / 1;
  margin-right: var(--gaia-row-gap);
}

.content {
  grid-area: 1 / 2;
}

/* Fixed at the micro step rather than inheriting the row: the trailing slot is
   metadata (a timestamp, a count, a kind), so it steps down from the title
   beside it whatever size the row itself is set to. */
.trailing {
  grid-area: 1 / 3;
  margin-left: var(--gaia-row-gap);
  font-size: v-bind(metaSize);
  color: v-bind(metaColor);
}

/* Under the content, never counted when centring the leading slot. No
   row-gap: the two lines' half-leading already separates them (the theme's
   1.55 line-height puts ~3px of air under the content and above the footer),
   so a gap on top of that reads as a gap twice its value. */
.footer {
  grid-area: 2 / 2;
  line-height: 1.3;
}

/* Rail row: hairline divider instead of GaiaCard's border. */
.flat {
  padding: 11px 2px;
  border-bottom: 1px solid v-bind(dividerColor);
  --gaia-row-gap: 10px;
}

.flat .content {
  line-height: 1.4;
  font-weight: v-bind(medium);
}

.flat.clickable {
  cursor: pointer;
}
</style>
